/**
 * Pool special bets page — searchable team Combobox replaces the raw 48-team
 * select; uses Card primitive, Badge, EmptyState, Skeleton, Alert, sticky header.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Star, Trophy, Clock, CheckCircle2, X } from 'lucide-react';
import Button from '../components/ui/Button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import Badge from '../components/ui/badge';
import PageHeader from '../components/ui/page-header';
import EmptyState from '../components/ui/empty-state';
import Skeleton from '../components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import Combobox, { type ComboboxOption } from '../components/ui/combobox';
import { TooltipProvider } from '../components/ui/tooltip';
import { usePool } from '../hooks/usePools';
import { useMySpecialPredictions, useSubmitSpecialPrediction } from '../hooks/usePredictions';
import { useLockCountdown } from '../hooks/useLockCountdown';
import { TEAMS } from '../lib/constants';
import { cn, formatDateTime } from '../lib/utils';
import type { SpecialBetWithPrediction } from '../../shared/types';
import { classifyPoolPageErrorState } from './pool-page-error-state';

export default function PoolSpecialsPage() {
  const { slug } = useParams<{ slug: string }>();
  const poolSlug = slug ?? '';
  const { data: poolData, error: poolError } = usePool(poolSlug);
  const pool = poolData?.pool;
  const { data: specialBets, isLoading, error: specialBetsError } =
    useMySpecialPredictions(poolSlug);
  const submitPrediction = useSubmitSpecialPrediction(poolSlug);

  const poolErrorState = classifyPoolPageErrorState({
    hasPool: Boolean(pool),
    error: poolError,
  });

  if (poolErrorState) {
    return (
      <EmptyState
        icon={<Star className="w-6 h-6" aria-hidden="true" />}
        title={poolErrorState.title}
        description={poolErrorState.description}
        action={
          <Link to="/boloes">
            <Button variant="outline">Voltar para bolões</Button>
          </Link>
        }
      />
    );
  }

  if (specialBetsError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Erro ao carregar palpites especiais</AlertTitle>
        <AlertDescription>
          Não foi possível carregar os palpites especiais agora.{' '}
          <Link to={`/boloes/${poolSlug}`} className="font-medium underline">
            Voltar para o bolão
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="max-w-3xl mx-auto">
        <PageHeader
          back={
            <Link
              to={`/boloes/${poolSlug}`}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para {pool?.name ?? 'o bolão'}
            </Link>
          }
          title="Palpites especiais"
          description="Aposte no campeão da Copa, no artilheiro e nos vencedores de grupo. Cada acerto vale pontos extras."
        />

        <Alert variant="info" className="mb-6">
          <AlertDescription>
            Esses palpites valem pontos extras e podem ser alterados até a data de
            encerramento exibida em cada card.
          </AlertDescription>
        </Alert>

        {isLoading ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-48 rounded-2xl" />
            ))}
          </div>
        ) : !specialBets || specialBets.length === 0 ? (
          <EmptyState
            icon={<Star className="w-6 h-6" aria-hidden="true" />}
            title="Nenhum palpite especial disponível"
            description="Os palpites especiais aparecerão aqui assim que forem liberados pelo administrador."
          />
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {specialBets.map((bet) => (
              <SpecialBetCard
                key={bet.id}
                bet={bet}
                onSubmit={(prediction) =>
                  submitPrediction.mutate({ betId: bet.id, prediction })
                }
                isSubmitting={submitPrediction.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

interface SpecialBetCardProps {
  bet: SpecialBetWithPrediction;
  onSubmit: (prediction: string) => void;
  isSubmitting: boolean;
}

function SpecialBetCard({ bet, onSubmit, isSubmitting }: SpecialBetCardProps) {
  const [selectedTeam, setSelectedTeam] = useState(bet.prediction || '');
  const [isEditing, setIsEditing] = useState(!bet.prediction);

  useEffect(() => {
    setSelectedTeam(bet.prediction || '');
    setIsEditing(!bet.prediction);
  }, [bet.prediction]);

  const lock = useLockCountdown(bet.lockTime);
  const locked = bet.isLocked || lock.locked;
  const hasPrediction = !!bet.prediction;
  const hasResult = !!bet.correctAnswer;
  const isCorrect = hasResult && bet.prediction === bet.correctAnswer;
  const isWrong = hasResult && bet.prediction !== bet.correctAnswer;

  const teamOptions = useMemo<ComboboxOption[]>(
    () =>
      TEAMS.map((t) => ({
        value: t.name,
        label: t.name,
        icon: <span aria-hidden="true">{t.flag}</span>,
        keywords: [t.code],
      })),
    []
  );

  const selectedTeamMeta = TEAMS.find((t) => t.name === bet.prediction);

  const icon =
    bet.type === 'CHAMPION' ? (
      <Trophy className="w-5 h-5" aria-hidden="true" />
    ) : (
      <Star className="w-5 h-5" aria-hidden="true" />
    );

  const iconBg =
    bet.type === 'CHAMPION'
      ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300'
      : bet.type === 'TOP_SCORER'
        ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300'
        : 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300';

  const handleSave = () => {
    if (!selectedTeam || locked) return;
    onSubmit(selectedTeam);
    setIsEditing(false);
  };

  return (
    <Card
      className={cn(
        'p-0',
        isCorrect && 'ring-2 ring-success',
        isWrong && 'ring-2 ring-destructive/40'
      )}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl', iconBg)}>
            {icon}
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-foreground leading-tight">
              {bet.description}
            </h3>
            <p className="text-xs text-primary-700 dark:text-primary-400 font-semibold">
              +{bet.pointsValue} pontos
            </p>
          </div>
        </div>
        {locked ? (
          <Badge variant="secondary">
            <Clock className="w-3 h-3" aria-hidden="true" />
            Encerrado
          </Badge>
        ) : !lock.locked && lock.totalSeconds < 86400 ? (
          <Badge variant={lock.urgent ? 'warning' : 'info'}>
            Trava em {lock.text}
          </Badge>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-3">
        {locked && !hasPrediction ? (
          <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
            <Clock className="w-6 h-6 opacity-50" aria-hidden="true" />
            <p className="text-sm">Apostas encerradas</p>
          </div>
        ) : isEditing && !locked ? (
          <div className="space-y-3">
            <Combobox
              ariaLabel={`Selecione um time para ${bet.description}`}
              value={selectedTeam}
              onValueChange={setSelectedTeam}
              options={teamOptions}
              placeholder="Selecione um time"
              searchPlaceholder="Buscar time…"
              emptyMessage="Nenhum time encontrado."
              disabled={isSubmitting}
            />
            <div className="flex gap-2">
              {hasPrediction && (
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  icon={<X />}
                  onClick={() => {
                    setSelectedTeam(bet.prediction || '');
                    setIsEditing(false);
                  }}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
              )}
              <Button
                type="button"
                className="flex-1"
                loading={isSubmitting}
                disabled={!selectedTeam}
                onClick={handleSave}
              >
                Salvar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-success" aria-hidden="true" />
                <span className="font-medium text-foreground truncate">
                  {selectedTeamMeta?.flag && (
                    <span aria-hidden="true" className="mr-1">
                      {selectedTeamMeta.flag}
                    </span>
                  )}
                  {bet.prediction}
                </span>
              </div>
              {!locked && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                >
                  Alterar
                </Button>
              )}
            </div>

            {hasResult && (
              <div
                className={cn(
                  'rounded-xl border px-4 py-3 text-sm',
                  isCorrect
                    ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30 text-green-900 dark:text-green-200'
                    : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 text-red-900 dark:text-red-200'
                )}
                role="status"
              >
                <p>
                  <strong>Resultado oficial:</strong> {bet.correctAnswer}
                  {isCorrect && ` — Você ganhou ${bet.pointsValue} pontos!`}
                  {isWrong && ' — Sua aposta não acertou.'}
                </p>
              </div>
            )}
          </div>
        )}

        {!locked && (
          <p className="text-[11px] text-muted-foreground">
            Encerra em: {formatDateTime(bet.lockTime)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
